import React, { useContext } from 'react'
import { Music4, SearchX, Star, History } from 'lucide-react';
import { CantiqueContextAPI } from '../Context/CantiqueContext';
import { hasCantiqueAudio } from '../utils/cantiqueAudio';
import { findLyricsExcerpt } from '../utils/search';
import Highlight from '../components/Highlight';

const CantiquesList = () => {
  const {
    filteredCantiques, allCantiques, catalogStatus, selectCantique, selectedCantique, loading, pendingCantiqueId, setSidebarOpen, viewMode,
    searchTerm, setSearchTerm, selectedLanguage, setSelectedLanguage,
    listMode, setListMode, favoriteCount, recentCount, isFavorite, clearRecents,
  } = useContext(CantiqueContextAPI);
  const collectionSuffix = listMode === 'favorites' ? ' dans vos favoris' : listMode === 'recents' ? ' dans les récents' : '';

  const handleSelectCantique = (id) => {
    // toggle off if clicking the currently selected item.
    if (selectedCantique?.id === id) {
      selectCantique(null);
    } else {
      selectCantique(id);
      // on mobile, close the sidebar immediately when selecting a cantique
      if (typeof window !== 'undefined' && window.innerWidth <= 1000) {
        setSidebarOpen(false);
      }
    }
  };

  return (
    <div className={`cantiqueList ${viewMode === 'number' ? 'cantiqueList--number' : ''}`}>
         <div className="cantiqueList__container">
           {filteredCantiques.length > 0 ? (
             filteredCantiques.map((cantique) => (
               <div 
                 key={cantique.id}
                 className={`cantiqueList__cantique ${selectedCantique?.id === cantique.id ? 'active' : ''}`}
                 onClick={() => handleSelectCantique(cantique.id)}
                 role="button"
                 tabIndex={0}
               >
                <div className="cantiqueList__cantique__left">
                  <div className="cantiqueList__number">{cantique.number || '-'}</div>
                  <div className="cantiqueList__texts">
                    <div className="cantiqueList__heading">
                      <div className="cantiqueList__title">
                        <Highlight text={cantique.title} query={searchTerm} />
                        {isFavorite(cantique.id) && (
                          <Star size={11} className="cantiqueList__favorite" aria-label="Favori" role="img" />
                        )}
                      </div>
                      <div className="cantiqueList__langue" style={{ fontSize: '0.75em', color: '#999', marginTop: '0.2em',
                        marginLeft: '0.3em'
                       }}>
                        {cantique.langue}
                      </div>
                    </div>
                    {searchTerm && viewMode !== 'number' && (() => {
                      const excerpt = findLyricsExcerpt(cantique, searchTerm);
                      return excerpt ? (
                        <div className="cantiqueList__excerpt"><Highlight text={excerpt} query={searchTerm} /></div>
                      ) : null;
                    })()}
                  </div>
                </div>

                 {(pendingCantiqueId === cantique.id && loading) || hasCantiqueAudio(cantique) ? (
                   <div className="cantiqueList__icon">
                     {pendingCantiqueId === cantique.id && loading ? (
                       <div className="loading-spinner" style={{width: '1.2em', height: '1.2em', borderWidth: '2px'}} />
                     ) : (
                       <Music4 size={15} className="cantiqueList__icon__music" />
                     )}
                   </div>
                 ) : null}
               </div>
             ))
           ) : catalogStatus === 'loading' ? (
             <div className="cantiqueList__empty" role="status" aria-label="Chargement des cantiques">
               <div className="loading-spinner" style={{ width: '1.4em', height: '1.4em', borderWidth: '2px' }} />
             </div>
           ) : catalogStatus === 'error' && allCantiques.length === 0 ? (
             <div className="cantiqueList__empty" role="alert">
               <p className="cantiqueList__empty__title">Cantiques indisponibles</p>
               <p className="cantiqueList__empty__text">Impossible de charger la liste. Vérifiez la connexion puis rechargez la page.</p>
             </div>
           ) : listMode === 'favorites' && favoriteCount === 0 ? (
             <div className="cantiqueList__empty" role="status">
               <Star size={22} className="cantiqueList__empty__icon" />
               <p className="cantiqueList__empty__title">Aucun favori pour l’instant</p>
               <p className="cantiqueList__empty__text">Touchez l’étoile sur la page d’un cantique pour le retrouver ici, même sans compte.</p>
               <div className="cantiqueList__empty__actions">
                 <button type="button" onClick={() => setListMode('all')}>Voir tous les cantiques</button>
               </div>
             </div>
           ) : listMode === 'recents' && recentCount === 0 ? (
             <div className="cantiqueList__empty" role="status">
               <History size={22} className="cantiqueList__empty__icon" />
               <p className="cantiqueList__empty__title">Aucun cantique consulté</p>
               <p className="cantiqueList__empty__text">Les cantiques que vous ouvrez apparaîtront ici.</p>
               <div className="cantiqueList__empty__actions">
                 <button type="button" onClick={() => setListMode('all')}>Voir tous les cantiques</button>
               </div>
             </div>
           ) : (
             <div className="cantiqueList__empty" role="status">
               <SearchX size={22} className="cantiqueList__empty__icon" />
               <p className="cantiqueList__empty__title">Aucun résultat</p>
               <p className="cantiqueList__empty__text">
                 {searchTerm.trim()
                   ? <>Aucun cantique ne correspond à « {searchTerm.trim()} »{selectedLanguage ? <> en {selectedLanguage}</> : null}{collectionSuffix}.</>
                   : <>Aucun cantique en {selectedLanguage}{collectionSuffix}.</>}
               </p>
               <div className="cantiqueList__empty__actions">
                 {searchTerm && (
                   <button type="button" onClick={() => setSearchTerm('')}>Effacer la recherche</button>
                 )}
                 {selectedLanguage && (
                   <button type="button" onClick={() => setSelectedLanguage(null)}>Toutes les langues</button>
                 )}
                 {listMode !== 'all' && (
                   <button type="button" onClick={() => setListMode('all')}>Voir tous les cantiques</button>
                 )}
               </div>
             </div>
           )}
           {listMode === 'recents' && recentCount > 0 && filteredCantiques.length > 0 && (
             <div className="cantiqueList__footerAction">
               <button type="button" onClick={clearRecents}>Effacer l’historique</button>
             </div>
           )}
         </div>
     </div>
  )
}

export default CantiquesList